import type {
  MassRenewalBuiltDivision,
  MassRenewalDivisionOutcome,
  MassRenewalDownloadFile,
  MassRenewalRun,
} from '@erp/rpc';

import { isRunActive } from '../hubbell-feedback/runDisplay';
import type { RenewalDropIssue } from './classifyRenewalFiles';

/** How the Mass Renewal card shows the org's latest run; pure, so the rules are testable without React. */

export type RenewalStartBlocker =
  'not allowed' | 'active run' | 'starting' | 'reading' | RenewalDropIssue;

/** Why Start is disabled, in the order the user can do least about; null when it may go. */
export function renewalStartBlocker(input: {
  drop: { reading: boolean; issues: readonly RenewalDropIssue[] };
  run: Pick<MassRenewalRun, 'status'> | null;
  /** The user may start a run (the Pricing create permission). */
  allowed: boolean;
  /** An upload-and-start is already under way from this page. */
  starting: boolean;
}): RenewalStartBlocker | null {
  if (!input.allowed) return 'not allowed';
  if (isRunActive(input.run)) return 'active run';
  if (input.starting) return 'starting';
  if (input.drop.reading) return 'reading';
  return input.drop.issues[0] ?? null;
}

/** The Divisions to show: the report once the run is done, the ones built so far before it. */
export function divisionOutcomes(
  run: Pick<MassRenewalRun, 'report' | 'progress'>,
): MassRenewalDivisionOutcome[] {
  return run.report?.divisions ?? run.progress;
}

/** Lines left out before pricing, every reason together. */
export function skippedLineCount(
  counts: MassRenewalBuiltDivision['counts'],
): number {
  return Object.values(counts.skipped).reduce((sum, count) => sum + count, 0);
}

/** The files a built Division offers: the exceptions CSV only when some line went to it. */
export function divisionDownloads(
  division: Pick<MassRenewalBuiltDivision, 'counts'>,
): MassRenewalDownloadFile[] {
  return division.counts.exceptions > 0
    ? ['upload', 'exceptions', 'report']
    : ['upload', 'report'];
}

/** Which download is in flight, so its button spins. */
export interface RenewalDownloadTarget {
  letter: string;
  file: MassRenewalDownloadFile;
}

export function isDownloading(
  downloading: RenewalDownloadTarget | null,
  letter: string,
  file: MassRenewalDownloadFile,
): boolean {
  return downloading?.letter === letter && downloading.file === file;
}
